import React, { useMemo } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { LoadableLazy } from '@confluence/loadable';

import { buildAtAGlanceSummaries } from '../../at-a-glance/buildAtAGlanceSummaries.ts';
import type { UseAtAGlanceResult } from '../../at-a-glance/useAtAGlance.ts';

import { AtAGlanceSection } from './AtAGlanceSection.tsx';
import { PeopleAndAgentsSection } from './PeopleAndAgentsSection.tsx';
import type { ConnectionSectionsData } from './useConnectionSectionsData.ts';
import { getSectionExpansionProps } from './useSectionExpansion.ts';
import type { SectionExpansionController } from './useSectionExpansion.ts';

const styles = cssMap({
	connectionsColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		boxSizing: 'border-box',
	},
});

// Keep section code out of the byline's eager imports. The shared menu item separately lazy-loads
// its Smart Link hover card and mounts each granted row only once that wrapper is ready.
const RelatedContentSection = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-RelatedContentSection" */ './RelatedContentSection.tsx'
			)
		).RelatedContentSection,
});

// Incoming and outgoing section code also stays behind its existing lazy boundary.
const LinksGroup = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-LinksGroup" */ './LinksGroup.tsx')).LinksGroup,
});

export type ConnectionSectionsProps = {
	onOverviewCommitted?: () => void;
	connections: ConnectionSectionsData;
	/**
	 * The cc-smarts response, fetched once by the surface that owns the connections. Not
	 * fetched here: the panel and the modal both render this component at the same time, so a
	 * call in here is a second paid LLM request for an answer already on screen.
	 */
	atAGlance: UseAtAGlanceResult;
	onPreviewOpen?: () => void;
	onAskRovo?: (prompt?: string) => void;
	/**
	 * Opens Rovo chat from the "Chat with Rovo" button at the end of "At a glance". Both surfaces
	 * pass it when Rovo is enabled, and both seed it with every connection url.
	 * Separate from `onAskRovo` because that one is bound to the `rowAction` trigger and seeds a
	 * single row's url.
	 */
	onAtAGlanceAskRovo?: () => void;
	/**
	 * Lifts each section's expanded state out to a caller that outlives the sections.
	 * Only the modal passes it, so its sections survive the switch to Rovo and back; the
	 * panel and popup omit it and let each section's shell own its own state.
	 */
	sectionExpansion?: SectionExpansionController;
};

/**
 * Both surfaces share the panel's data to avoid duplicate link and paid summary requests.
 * Only Related content rows show one-liners. Appears in and Links to stay in the summary
 * request as evidence for the overview, even though their rows do not show one-liners.
 */
export const ConnectionSections = ({
	connections,
	atAGlance,
	onPreviewOpen,
	onAskRovo,
	onAtAGlanceAskRovo,
	sectionExpansion,
	onOverviewCommitted,
}: ConnectionSectionsProps): React.JSX.Element => {
	const aiSummaries = useMemo(() => buildAtAGlanceSummaries(atAGlance.output), [atAGlance.output]);
	const peopleAndAgentsLoading =
		connections.people.loading ||
		connections.agentViewers.loading ||
		connections.agentContributors.loading;
	const peopleAndAgentsHasError = Boolean(
		connections.people.error ||
		connections.agentViewers.error ||
		connections.agentContributors.error ||
		connections.agentContributors.availability === 'unavailable',
	);

	return (
		<Box xcss={styles.connectionsColumn}>
			{/* The card rolls out on its own gate so the Related content one-liners, which come from
			    the same response, can ship first. It also honours the cc-smarts kill switch the byline
			    checks before requesting: with no request there is no summary, and the card would only
			    show its no-summary copy. The kill switch is checked here in code rather than as a
			    Statsig rule on the card gate so the dependency needs no flag-rule change review; the
			    cost is a card-gate exposure logged for kill-switched tenants who never see the card. */}
			{/* oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates, confluence-feature-gating/no-preconditioning */}
			{fg('confluence_twg_at_a_glance_summary') && fg('cc_smarts_twg_at_a_glance_enabled') ? (
				<AtAGlanceSection
					{...atAGlance}
					onAskRovo={onAtAGlanceAskRovo}
					onOverviewCommitted={onOverviewCommitted}
				/>
			) : null}
			<RelatedContentSection
				{...connections.relatedContent}
				aiSummaries={aiSummaries}
				onPreviewOpen={onPreviewOpen}
				onAskRovo={onAskRovo}
				{...getSectionExpansionProps(sectionExpansion, 'relatedContent')}
			/>
			<LinksGroup
				syncedFrom={connections.syncedFrom}
				appearsIn={connections.appearsIn}
				linksTo={connections.linksTo}
				onPreviewOpen={onPreviewOpen}
				onAskRovo={onAskRovo}
				sectionExpansion={sectionExpansion}
			/>
			<PeopleAndAgentsSection
				people={connections.people.peopleAndAgents}
				loading={peopleAndAgentsLoading}
				hasError={peopleAndAgentsHasError}
				{...getSectionExpansionProps(sectionExpansion, 'people')}
			/>
		</Box>
	);
};
